import { CircleFlag } from "react-circle-flags";
import styles from "./selector.module.css";
import { IoIosArrowDown } from "react-icons/io";
import { countries, getCountryCode } from "../data/countries";
import { useEffect, useRef, useState } from "react";
import { handleUpdateCountryClick } from "../handlers";
import { useUserDataContext } from "../user-data-context";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { useLocale, useTranslations } from "next-intl";

export default function CountrySelector() {
  const { userData } = useUserAuthDataContext();
  const { state, updateField } = useUserDataContext();
  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef<HTMLDivElement | null>(null);
  const t = useTranslations("CountrySelector");
  const locale = useLocale();
  const displayNames = new Intl.DisplayNames([locale], { type: "region" });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        toggleRef.current &&
        !toggleRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredCountries = countries.filter((country) =>
    (displayNames.of(country.Code.toUpperCase()) ?? "")
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const selectedCountryCode = normalizeCountryCode(state.country);
  const selectedCountryName = selectedCountryCode
    ? displayNames.of(selectedCountryCode.toUpperCase())
    : t("notProvided");

  return (
    <div
      ref={toggleRef}
      className={styles.selector}
      role="combobox"
      aria-expanded={isOpen}
      aria-label={t("ariaCountrySelector")}
    >
      <div
        className={styles.selectorWrapper}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        role="button"
        aria-label={t("ariaToggleCountryList")}
      >
        {selectedCountryCode && (
          <CircleFlag
            countryCode={selectedCountryCode.toLocaleLowerCase()}
            className={styles.countryIcon}
            aria-hidden="true"
          />
        )}
        <p>
          {selectedCountryName !== undefined
            ? selectedCountryName
            : state.country}
        </p>
        <IoIosArrowDown className={styles.arrowDown} aria-hidden="true" />
      </div>
      {isOpen && (
        <div className={styles.selectorItemList} role="listbox">
          <input
            type="text"
            placeholder={t("inputPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
            aria-label={t("ariaSearchCountry")}
          />
          {filteredCountries.map((country) => {
            const countryName =
              displayNames.of(country.Code.toUpperCase()) ?? country.Name;
            return (
              <div
                className={styles.selectorItemWrapper}
                key={country.Code}
                role="option"
                aria-label={t("ariaSelectCountry", {
                  country: countryName,
                })}
              >
                <div
                  className={styles.selectorItem}
                  onClick={async () => {
                    if (state.country === country.Name) {
                      setIsOpen(false);
                      return;
                    }
                    await handleUpdateCountryClick(
                      state.country,
                      country.Code,
                      (newValue) => {
                        updateField("country", newValue);
                      },
                      () => {
                        setIsOpen(false);
                      },
                      userData.sessionId
                    );
                  }}
                >
                  <CircleFlag
                    countryCode={country.Code.toLocaleLowerCase()}
                    className={styles.countryIcon}
                    aria-hidden="true"
                  />
                  <p>{countryName}</p>
                </div>
                <div className={styles.separator}></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function normalizeCountryCode(value?: string): string | undefined {
  if (!value) return undefined;

  // valid ISO-2 code
  if (value.length === 2) {
    return value.toLowerCase();
  }

  // legacy name → try to map
  const match = countries.find(
    (c) => c.Name.toLowerCase() === value.toLowerCase()
  );

  return match?.Code.toLowerCase();
}
